import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaCalendarAlt, FaClock, FaInfoCircle, FaCheckCircle, FaTimesCircle, FaHourglassHalf, FaUsers, FaCog, FaTimes, FaDownload, FaTrash, FaPen, FaUndo, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const statusStyles = {
  Afventer: {
    badge: 'bg-yellow-100 text-yellow-800',
    dot: '🟡'
  },
  Godkendt: {
    badge: 'bg-green-100 text-green-800',
    dot: '🟢'
  },
  Afslået: {
    badge: 'bg-red-100 text-red-800',
    dot: '🔴'
  }
};

const weekdaysBetween = (start, end, holidaySet = new Set()) => {
  if (!start || !end) return 0;
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s) || Number.isNaN(e) || s > e) return 0;
  const cursor = new Date(s);
  let count = 0;
  while (cursor <= e) {
    const day = cursor.getDay();
    const iso = cursor.toISOString().slice(0, 10);
    if (day !== 0 && day !== 6 && !holidaySet.has(iso)) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

const formatDate = (value) => value ? new Date(value).toLocaleDateString('da-DK') : '';
const formatTime = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 5);
  return '';
};
const formatRangeWithTime = (startDate, endDate, startTime, endTime, startDayEndTime) => {
  if (!startDate || !endDate) return '';
  const startLabel = formatDate(startDate);
  const endLabel = formatDate(endDate);
  const startTimeLabel = formatTime(startTime);
  const endTimeLabel = formatTime(endTime);
  const startDayEndLabel = formatTime(startDayEndTime);
  const sameDay = toIsoDate(startDate) === toIsoDate(endDate);

  if (sameDay) {
    if (startTimeLabel && endTimeLabel) return `${startLabel} kl. ${startTimeLabel}-${endTimeLabel}`;
    if (startTimeLabel) return `${startLabel} fra kl. ${startTimeLabel}`;
    if (endTimeLabel) return `${startLabel} til kl. ${endTimeLabel}`;
    return startLabel;
  }

  const startSuffix = startTimeLabel && startDayEndLabel
    ? ` kl. ${startTimeLabel}-${startDayEndLabel}`
    : startTimeLabel
      ? ` kl. ${startTimeLabel}`
      : '';
  const endSuffix = endTimeLabel ? ` kl. ${endTimeLabel}` : '';
  return `${startLabel}${startSuffix} – ${endLabel}${endSuffix}`;
};

const toIsoDate = (date) => {
  const d = new Date(date);
  if (Number.isNaN(d)) return '';
  const year = d.getFullYear();
  const month = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const RangeCalendar = ({ startDate, endDate, onChange, holidaySet = new Set(), holidayByDate = new Map(), onHolidaySelect }) => {
  const initialMonth = startDate || endDate || new Date();
  const [visibleMonth, setVisibleMonth] = useState(new Date(initialMonth));
  const today = useMemo(() => new Date(), []);

  useEffect(() => {
    const nextMonth = startDate || endDate;
    if (nextMonth) {
      setVisibleMonth(new Date(nextMonth));
    }
  }, [startDate, endDate]);

  const start = startDate ? new Date(startDate) : null;
  const end = endDate ? new Date(endDate) : null;

  const handleDayClick = (date) => {
    const iso = toIsoDate(date);
    const label = holidayByDate.get(iso);
    if (onHolidaySelect) {
      onHolidaySelect(label || '');
    }
    if (!start || (start && end)) {
      onChange({ startDate: iso, endDate: '' });
      return;
    }
    if (start && !end) {
      const selected = new Date(date);
      if (selected < start) {
        onChange({ startDate: iso, endDate: toIsoDate(start) });
      } else {
        onChange({ startDate: toIsoDate(start), endDate: iso });
      }
    }
  };

  const monthStart = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1);
  const monthEnd = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 0);
  const startWeekday = (monthStart.getDay() + 6) % 7; // make Monday = 0
  const daysInMonth = monthEnd.getDate();
  const days = [];
  for (let i = 0; i < startWeekday; i += 1) {
    days.push(null);
  }
  for (let d = 1; d <= daysInMonth; d += 1) {
    days.push(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), d));
  }
  while (days.length % 7 !== 0) {
    days.push(null);
  }

  const isSameDay = (a, b) => a && b && a.toDateString() === b.toDateString();
  const inRange = (day) => {
    if (!start || !end || !day) return false;
    return day >= start && day <= end;
  };

  const weekdays = ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'];

  return (
    <div className="border border-gray-300 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-800">
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
          onClick={() => setVisibleMonth(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() - 1, 1))}
          aria-label="Forrige måned"
        >
          <FaChevronLeft />
        </button>
        <div className="font-semibold text-gray-900 dark:text-gray-100">
          {visibleMonth.toLocaleDateString('da-DK', { month: 'long', year: 'numeric' })}
        </div>
        <button
          type="button"
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
          onClick={() => setVisibleMonth(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1))}
          aria-label="Næste måned"
        >
          <FaChevronRight />
        </button>
      </div>
      <div className="grid grid-cols-7 text-center text-xs font-semibold text-gray-500 mb-2">
        {weekdays.map((day) => (
          <div key={day}>{day}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1 text-sm">
        {days.map((day, idx) => {
          const isStart = isSameDay(day, start);
          const isEnd = isSameDay(day, end);
          const between = inRange(day);
          const isSelected = isStart || isEnd || between;
          const isToday = isSameDay(day, today);
          const dateIso = day ? toIsoDate(day) : '';
          const isHoliday = day ? holidaySet.has(dateIso) : false;
          const holidayLabel = isHoliday ? holidayByDate.get(dateIso) : '';
          return (
            <button
              key={idx}
              type="button"
              disabled={!day}
              onClick={() => day && handleDayClick(day)}
              className={`h-9 rounded-md flex items-center justify-center transition-colors border
                ${day ? 'border-transparent' : 'border-transparent cursor-default'}
                ${isSelected ? 'bg-blue-100 text-blue-900 border-blue-300' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'}
                ${isStart || isEnd ? 'font-semibold ring-1 ring-blue-400' : ''}
                ${isToday && !isStart && !isEnd ? 'ring-1 ring-orange-400' : ''}
                ${!isSelected && isHoliday ? 'bg-amber-100 text-amber-900 ring-1 ring-amber-400' : ''}
              `}
              title={holidayLabel || undefined}
            >
              {day ? day.getDate() : ''}
            </button>
          );
        })}
      </div>
      <div className="mt-3 text-xs text-gray-600 dark:text-gray-300 flex justify-between">
        <span>Start: {startDate ? formatDate(startDate) : '–'}</span>
        <span>Slut: {endDate ? formatDate(endDate) : '–'}</span>
      </div>
      <div className="mt-2 text-[11px] text-amber-800 dark:text-amber-300">
        Helligdage/fridage er markeret med gult og tæller ikke med i dagberegningen.
      </div>
    </div>
  );
};

const Afspadsering = () => {
  const [overview, setOverview] = useState([]);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [myRequests, setMyRequests] = useState([]);
  const [myLoading, setMyLoading] = useState(true);
  const [adminRequests, setAdminRequests] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [formData, setFormData] = useState({
    startDate: '',
    endDate: '',
    startTime: '',
    startDayEndTime: '',
    endTime: '',
    comment: ''
  });
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [holidayDrafts, setHolidayDrafts] = useState([]);
  const [holidayNotice, setHolidayNotice] = useState('');
  const [editHolidayNotice, setEditHolidayNotice] = useState('');
  const [days, setDays] = useState(0);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [fromFilter, setFromFilter] = useState('');
  const [toFilter, setToFilter] = useState('');
  const [decisionComments, setDecisionComments] = useState({});
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [approverEmail, setApproverEmail] = useState('');
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [settingsMessage, setSettingsMessage] = useState('');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [editData, setEditData] = useState({
    startDate: '',
    endDate: '',
    startTime: '',
    startDayEndTime: '',
    endTime: '',
    comment: ''
  });
  const [editDays, setEditDays] = useState(0);
  const [entryActionLoading, setEntryActionLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const markAfspadseringViewed = useCallback(() => {
    try {
      const now = Date.now();
      localStorage.setItem('afspadseringLastViewed', String(now));
      window.dispatchEvent(new Event('afspadsering-last-viewed'));
    } catch (_) {
      // ignore storage issues
    }
  }, []);

  const token = useMemo(() => localStorage.getItem('token'), []);
  const role = useMemo(() => localStorage.getItem('userRole') || 'bruger', []);
  const isAdmin = role === 'admin';
  const holidaySet = useMemo(() => new Set(holidays.map((h) => h.date)), [holidays]);
  const holidayByDate = useMemo(() => {
    const map = new Map();
    holidays.forEach((h) => {
      if (h?.date) map.set(h.date, h.label || h.date);
    });
    return map;
  }, [holidays]);
  const pendingRequests = useMemo(() => adminRequests.filter((req) => req.status === 'Afventer'), [adminRequests]);
  const showAdminSection = isAdmin && (adminLoading || pendingRequests.length > 0);

  const headers = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };

  useEffect(() => {
    setDays(weekdaysBetween(formData.startDate, formData.endDate, holidaySet));
  }, [formData.startDate, formData.endDate, holidaySet]);

  useEffect(() => {
    setEditDays(weekdaysBetween(editData.startDate, editData.endDate, holidaySet));
  }, [editData.startDate, editData.endDate, holidaySet]);

  const loadOverview = async () => {
    setOverviewLoading(true);
    try {
      const query = isAdmin ? '?includePending=true' : '';
      const res = await fetch(`/api/afspadsering/overview${query}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error('Kunne ikke hente oversigten');
      const data = await res.json();
      setOverview(data);
    } catch (e) {
      console.error(e);
      setOverview([]);
    } finally {
      setOverviewLoading(false);
    }
  };

  const loadMyRequests = async () => {
    setMyLoading(true);
    try {
      const res = await fetch('/api/afspadsering/mine', { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error('Kunne ikke hente dine forespørgsler');
      const data = await res.json();
      setMyRequests(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setMyRequests([]);
    } finally {
      setMyLoading(false);
    }
  };

  const loadHolidays = useCallback(async () => {
    try {
      const res = await fetch('/api/afspadsering/holidays');
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Kunne ikke hente helligdage');
      if (Array.isArray(data?.holidays)) {
        setHolidays(data.holidays);
      }
    } catch (err) {
      console.error(err);
      setHolidays([]);
    }
  }, []);

  const loadAdminRequests = async () => {
    if (!isAdmin) return;
    setAdminLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('status', 'Afventer');
      if (fromFilter) params.set('from', fromFilter);
      if (toFilter) params.set('to', toFilter);
      const res = await fetch(`/api/afspadsering/admin?${params.toString()}`, { headers });
      if (!res.ok) throw new Error('Kunne ikke hente forespørgsler');
      const data = await res.json();
      setAdminRequests(Array.isArray(data) ? data : []);
      markAfspadseringViewed();
    } catch (e) {
      console.error(e);
      setAdminRequests([]);
    } finally {
      setAdminLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    if (!token) return;
    try {
      const res = await fetch('/api/auth/verify', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data?.user) {
        setCurrentUser(data.user);
        setSelectedUserId((prev) => prev || data.user.id);
      }
    } catch (err) {
      console.error('Kunne ikke hente brugerinfo', err);
    }
  };

  const fetchUsers = async () => {
    if (!isAdmin || !token) return;
    setUsersLoading(true);
    try {
      const res = await fetch('/api/indstillinger/brugere', { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Kunne ikke hente brugere');
      const mapped = (Array.isArray(data) ? data : []).map((u) => ({
        id: u.id,
        label: u.name || u.brugernavn || u.email || `Bruger #${u.id}`
      }));
      mapped.sort((a, b) => a.label.localeCompare(b.label, 'da'));
      setUserOptions(mapped);
    } catch (err) {
      console.error(err);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    loadOverview();
    loadMyRequests();
    loadAdminRequests();
    loadHolidays();
    fetchCurrentUser();
    if (isAdmin) {
      fetchSettings();
      fetchUsers();
      markAfspadseringViewed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAdmin) {
      loadAdminRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromFilter, toFilter]);

  useEffect(() => {
    if (currentUser && !selectedUserId) {
      setSelectedUserId(currentUser.id);
    }
  }, [currentUser, selectedUserId]);

  const isFormMultiDay = formData.startDate && formData.endDate && toIsoDate(formData.startDate) !== toIsoDate(formData.endDate);
  const isEditMultiDay = editData.startDate && editData.endDate && toIsoDate(editData.startDate) !== toIsoDate(editData.endDate);

  useEffect(() => {
    if (!isFormMultiDay && formData.startDayEndTime) {
      setFormData((prev) => ({ ...prev, startDayEndTime: '' }));
    }
  }, [isFormMultiDay, formData.startDayEndTime]);

  useEffect(() => {
    if (!isEditMultiDay && editData.startDayEndTime) {
      setEditData((prev) => ({ ...prev, startDayEndTime: '' }));
    }
  }, [isEditMultiDay, editData.startDayEndTime]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!formData.startDate || !formData.endDate) {
      setError('Vælg start- og slutdato i kalenderen.');
      return;
    }
    const sameDay = toIsoDate(formData.startDate) === toIsoDate(formData.endDate);
    if (sameDay && formData.startDayEndTime) {
      setError('Sluttid for første dag kan kun angives ved flere dage.');
      return;
    }
    if (formData.startDayEndTime && !formData.startTime) {
      setError('Angiv starttid når der angives sluttid for første dag.');
      return;
    }
    if (formData.startDayEndTime && formData.startTime >= formData.startDayEndTime) {
      setError('Sluttid for første dag skal være efter starttid.');
      return;
    }
    if (sameDay && (formData.startTime || formData.endTime)) {
      if (!formData.startTime || !formData.endTime) {
        setError('Angiv både start- og sluttid for samme dag.');
        return;
      }
      if (formData.startTime >= formData.endTime) {
        setError('Sluttid skal være efter starttid.');
        return;
      }
    }
    try {
      const payload = { ...formData };
      if (isAdmin && selectedUserId) {
        payload.userId = selectedUserId;
      }
      const res = await fetch('/api/afspadsering', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.message || 'Kunne ikke sende forespørgsel');
      }
      setMessage('Din forespørgsel er sendt til godkendelse.');
      setFormData({ startDate: '', endDate: '', startTime: '', startDayEndTime: '', endTime: '', comment: '' });
      setSelectedUserId(currentUser?.id || null);
      setDays(0);
      loadMyRequests();
      loadOverview();
    } catch (err) {
      setError(err.message || 'Der opstod en fejl');
    }
  };
  const handleDecision = async (id, status) => {
    try {
      const res = await fetch(`/api/afspadsering/${id}/status`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ status, adminComment: decisionComments[id] || '' })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Kunne ikke opdatere status');
      setMessage('Status opdateret');
      loadAdminRequests();
      loadOverview();
      loadMyRequests();
    } catch (err) {
      setError(err.message || 'Fejl under opdatering');
    }
  };

  const fetchSettings = async () => {
    if (!isAdmin) return;
    setSettingsLoading(true);
    setSettingsError('');
    try {
      const res = await fetch('/api/afspadsering/settings', { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Kunne ikke hente indstillinger');
      setApproverEmail(data.approver_email || '');
      setHolidays(data.holidays || []);
      setHolidayDrafts(data.holidays || []);
      setSettingsLoaded(true);
    } catch (err) {
      setSettingsError(err.message || 'Fejl ved indlæsning af indstillinger');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleExport = async () => {
    if (!isAdmin || exporting) return;
    setError('');
    setMessage('');
    setExporting(true);
    try {
      const res = await fetch('/api/afspadsering/export', { headers });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.message || 'Kunne ikke eksportere afspadseringer');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
    } catch (err) {
      setError(err.message || 'Kunne ikke eksportere afspadseringer');
    } finally {
      setExporting(false);
    }
  };

  const openEntry = (entry) => {
    if (!isAdmin) return;
    setSelectedEntry(entry);
    setEditData({
      startDate: entry.start_date,
      endDate: entry.end_date,
      startTime: formatTime(entry.start_time),
      startDayEndTime: formatTime(entry.start_day_end_time),
      endTime: formatTime(entry.end_time),
      comment: entry.comment || ''
    });
    setEditDays(entry.days || weekdaysBetween(entry.start_date, entry.end_date));
  };

  const closeEntryModal = () => {
    setSelectedEntry(null);
    setEditData({ startDate: '', endDate: '', startTime: '', startDayEndTime: '', endTime: '', comment: '' });
    setEditDays(0);
  };

  const refreshAll = () => {
    loadOverview();
    loadAdminRequests();
    loadMyRequests();
  };

  const handleUpdateEntry = async () => {
    if (!selectedEntry) return;
    if (!editData.startDate || !editData.endDate) {
      setError('Vælg start- og slutdato i kalenderen.');
      return;
    }
    const sameDay = toIsoDate(editData.startDate) === toIsoDate(editData.endDate);
    if (sameDay && editData.startDayEndTime) {
      setError('Sluttid for første dag kan kun angives ved flere dage.');
      return;
    }
    if (editData.startDayEndTime && !editData.startTime) {
      setError('Angiv starttid når der angives sluttid for første dag.');
      return;
    }
    if (editData.startDayEndTime && editData.startTime >= editData.startDayEndTime) {
      setError('Sluttid for første dag skal være efter starttid.');
      return;
    }
    if (sameDay && (editData.startTime || editData.endTime)) {
      if (!editData.startTime || !editData.endTime) {
        setError('Angiv både start- og sluttid for samme dag.');
        return;
      }
      if (editData.startTime >= editData.endTime) {
        setError('Sluttid skal være efter starttid.');
        return;
      }
    }
    setEntryActionLoading(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/afspadsering/${selectedEntry.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          startDate: editData.startDate,
          endDate: editData.endDate,
          startTime: editData.startTime || '',
          startDayEndTime: editData.startDayEndTime || '',
          endTime: editData.endTime || '',
          comment: editData.comment
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Kunne ikke opdatere anmodning');
      setMessage('Anmodningen er opdateret og sendt til godkendelse.');
      closeEntryModal();
      refreshAll();
    } catch (err) {
      setError(err.message || 'Kunne ikke opdatere anmodning');
    } finally {
      setEntryActionLoading(false);
    }
  };

  const handleResetEntry = async () => {
    if (!selectedEntry) return;
    setEntryActionLoading(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/afspadsering/${selectedEntry.id}/reset`, {
        method: 'POST',
        headers
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Kunne ikke flytte til afventer');
      setMessage('Anmodningen er sendt til godkendelse igen.');
      closeEntryModal();
      refreshAll();
    } catch (err) {
      setError(err.message || 'Kunne ikke flytte til afventer');
    } finally {
      setEntryActionLoading(false);
    }
  };

  const handleDeleteEntry = async () => {
    if (!selectedEntry) return;
    setEntryActionLoading(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/afspadsering/${selectedEntry.id}`, {
        method: 'DELETE',
        headers
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.message || 'Kunne ikke slette anmodning');
      }
      setMessage('Anmodningen er slettet.');
      closeEntryModal();
      refreshAll();
    } catch (err) {
      setError(err.message || 'Kunne ikke slette anmodning');
    } finally {
      setEntryActionLoading(false);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSettingsSaving(true);
    setSettingsError('');
    setSettingsMessage('');
    try {
      const res = await fetch('/api/afspadsering/settings', {
        method: 'POST',
        headers,
        body: JSON.stringify({ approver_email: approverEmail, holidays: holidayDrafts })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Kunne ikke gemme indstillinger');
      setSettingsMessage('Indstillinger gemt');
      setHolidays(data.holidays || []);
      setHolidayDrafts(data.holidays || []);
      setSettingsLoaded(true);
    } catch (err) {
      setSettingsError(err.message || 'Der opstod en fejl');
    } finally {
      setSettingsSaving(false);
    }
  };

  const addHolidayDraft = () => {
    setHolidayDrafts([...holidayDrafts, { date: '', label: '' }]);
  };

  const updateHolidayDraft = (idx, field, value) => {
    setHolidayDrafts((prev) => prev.map((item, i) => (i === idx ? { ...item, [field]: value } : item)));
  };

  const removeHolidayDraft = (idx) => {
    setHolidayDrafts((prev) => prev.filter((_, i) => i !== idx));
  };

  const statusBadge = (status) => {
    const styles = statusStyles[status] || statusStyles.Afventer;
    return (
      <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${styles.badge}`}>
        <span className="mr-1">{styles.dot}</span>
        {status}
      </span>
    );
  };

  const publicEntries = overview.filter((item) => item.status === 'Godkendt');
  const adminVisibleEntries = isAdmin ? overview : publicEntries;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Fælles kalender</h2>
                {isAdmin && (
                  <>
                    <button
                      type="button"
                      onClick={handleExport}
                      className="ml-2 inline-flex items-center justify-center p-0 bg-transparent border-0 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 focus:outline-none focus:ring-0"
                      aria-label="Eksporter kommende afspadseringer"
                      disabled={exporting}
                    >
                      <FaDownload />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSettingsOpen(true);
                        setHolidayDrafts(holidays);
                        if (!settingsLoaded) {
                          fetchSettings();
                        }
                      }}
                      className="ml-2 inline-flex items-center justify-center p-0 bg-transparent border-0 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 focus:outline-none focus:ring-0"
                      aria-label="Afspadseringsindstillinger"
                    >
                      <FaCog />
                    </button>
                  </>
                )}
              </div>
              <p className="text-gray-500 text-sm">Alle kan se godkendt afspadsering. Afventer vises kun for admin.</p>
            </div>
            <FaCalendarAlt className="text-falck-blue text-2xl" />
          </div>
          {overviewLoading ? (
            <div className="flex items-center justify-center py-10 text-gray-500">Indlæser oversigt...</div>
          ) : (
            <div className="space-y-3">
              {adminVisibleEntries.length === 0 && (
                <div className="text-gray-500 text-sm">Ingen afspadseringer fundet.</div>
              )}
              {adminVisibleEntries.map((entry) => (
              <div
                key={entry.id}
                className={`p-3 border border-gray-200 dark:border-gray-800 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${isAdmin ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800' : ''}`}
                onClick={() => openEntry(entry)}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-gray-900 dark:text-gray-100 font-semibold">
                    <FaUsers className="text-falck-blue" />
                      <span>{entry.display_name}</span>
                      {entry.crew_number && (
                        <span className="text-xs text-gray-500">#{entry.crew_number}</span>
                      )}
                      {statusBadge(entry.status)}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                      {formatRangeWithTime(entry.start_date, entry.end_date, entry.start_time, entry.end_time, entry.start_day_end_time)} ({entry.days} dage)
                    </div>
                    {isAdmin && entry.comment && (
                      <div className="text-xs text-gray-500 mt-1">Note: {entry.comment}</div>
                    )}
                  </div>
                  {entry.status === 'Afventer' && isAdmin && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Afventer godkendelse</span>
                      <FaHourglassHalf className="text-yellow-500" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow p-4 sm:p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Anmod om afspadsering</h2>
            <FaClock className="text-falck-blue" />
          </div>
          {message && <div className="p-2 bg-green-100 text-green-800 rounded text-sm">{message}</div>}
          {error && <div className="p-2 bg-red-100 text-red-800 rounded text-sm">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-3">
            {isAdmin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Medarbejder</label>
                <select
                  value={selectedUserId || ''}
                  onChange={(e) => setSelectedUserId(e.target.value ? Number(e.target.value) : null)}
                  className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-falck-blue"
                  disabled={usersLoading}
                >
                  {currentUser && (
                    <option value={currentUser.id}>
                      Mig selv ({currentUser.name || currentUser.username || currentUser.brugernavn || currentUser.email || currentUser.id})
                    </option>
                  )}
                  {userOptions
                    .filter((u) => !currentUser || u.id !== currentUser.id)
                    .map((user) => (
                      <option key={user.id} value={user.id}>{user.label}</option>
                    ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Vælg hvem anmodningen oprettes for.</p>
              </div>
            )}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Periode</label>
              <RangeCalendar
                startDate={formData.startDate}
                endDate={formData.endDate}
                holidaySet={holidaySet}
                holidayByDate={holidayByDate}
                onHolidaySelect={setHolidayNotice}
                onChange={({ startDate, endDate }) => setFormData({ ...formData, startDate, endDate })}
              />
              {holidayNotice && (
                <div className="text-xs text-amber-700 dark:text-amber-300 mt-1">Helligdag/fridag: {holidayNotice}</div>
              )}
            </div>
            <div className={`grid grid-cols-1 ${isFormMultiDay ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-2`}>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Starttid (valgfri)</label>
                <input
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                  className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-falck-blue"
                />
              </div>
              {isFormMultiDay && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Sluttid første dag (valgfri)</label>
                  <input
                    type="time"
                    value={formData.startDayEndTime}
                    onChange={(e) => setFormData({ ...formData, startDayEndTime: e.target.value })}
                    className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-falck-blue"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  {isFormMultiDay ? 'Sluttid sidste dag (valgfri)' : 'Sluttid (valgfri)'}
                </label>
                <input
                  type="time"
                  value={formData.endTime}
                  onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                  className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-falck-blue"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500">
              Starttid gælder første dag. Sluttid sidste dag gælder kun for den sidste dag. Ved delvis første dag kan du angive sluttid første dag.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Kommentar til leder (valgfri)</label>
              <textarea
                value={formData.comment}
                onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                rows={3}
                className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-falck-blue"
                placeholder="Evt. note til leder"
              />
            </div>
            <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-300">
              <span>Antal hverdage: <strong>{days}</strong></span>
              <span className="flex items-center gap-1 text-xs text-gray-500"><FaInfoCircle /> Weekender tælles ikke med</span>
            </div>
            <button
              type="submit"
              className="w-full bg-falck-blue hover:bg-falck-dark-blue text-white py-2 rounded-md font-semibold"
            >
              Send forespørgsel
            </button>
          </form>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow p-4 sm:p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Mine forespørgsler</h3>
          <FaInfoCircle className="text-gray-400" />
        </div>
        {myLoading ? (
          <div className="text-gray-500">Indlæser...</div>
        ) : myRequests.length === 0 ? (
          <div className="text-gray-500 text-sm">Du har ikke lavet forespørgsler endnu.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-3 py-2 text-left">Periode</th>
                  <th className="px-3 py-2 text-left">Dage</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Kommentar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {myRequests.map((req) => (
                  <tr key={req.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-3 py-2">{formatRangeWithTime(req.start_date, req.end_date, req.start_time, req.end_time, req.start_day_end_time)}</td>
                    <td className="px-3 py-2">{req.days}</td>
                    <td className="px-3 py-2">{statusBadge(req.status)}</td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-300 text-xs">
                      {req.status === 'Afslået' && req.admin_comment ? req.admin_comment : (req.comment || '–')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAdminSection && (
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow p-4 sm:p-6 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Forespørgsler (admin)</h3>
              <p className="text-sm text-gray-500">Filtrer afventende forespørgsler og godkend/afslå direkte.</p>
            </div>
            <div className="flex flex-wrap gap-2 text-sm">
              <label className="flex items-center gap-1">
                <span>Fra</span>
                <input type="date" value={fromFilter} onChange={(e) => setFromFilter(e.target.value)} className="border border-gray-300 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-800" />
              </label>
              <label className="flex items-center gap-1">
                <span>Til</span>
                <input type="date" value={toFilter} onChange={(e) => setToFilter(e.target.value)} className="border border-gray-300 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-800" />
              </label>
            </div>
          </div>

          {adminLoading ? (
            <div className="text-gray-500">Indlæser forespørgsler...</div>
          ) : pendingRequests.length === 0 ? (
            <div className="text-gray-500 text-sm">Ingen forespørgsler matcher dine filtre.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-3 py-2 text-left">Medarbejder</th>
                    <th className="px-3 py-2 text-left">Periode</th>
                    <th className="px-3 py-2 text-left">Dage</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Kommentar</th>
                    <th className="px-3 py-2 text-left">Handling</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {pendingRequests.map((req) => (
                    <tr key={req.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900 dark:text-gray-100">{req.display_name}</div>
                        <div className="text-xs text-gray-500">#{req.crew_number || 'N/A'}</div>
                      </td>
                      <td className="px-3 py-2">{formatRangeWithTime(req.start_date, req.end_date, req.start_time, req.end_time, req.start_day_end_time)}</td>
                      <td className="px-3 py-2">{req.days}</td>
                      <td className="px-3 py-2">{statusBadge(req.status)}</td>
                      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300">
                        <div>Ansøger: {req.comment || '–'}</div>
                        {req.admin_comment && <div className="text-red-600">Leder: {req.admin_comment}</div>}
                      </td>
                      <td className="px-3 py-2 space-y-2 min-w-[180px]">
                        <textarea
                          rows={2}
                          placeholder="Kommentar til svar (valgfri)"
                          value={decisionComments[req.id] || ''}
                          onChange={(e) => setDecisionComments({ ...decisionComments, [req.id]: e.target.value })}
                          className="w-full border border-gray-300 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-800"
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleDecision(req.id, 'Godkendt')}
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded flex items-center justify-center gap-1"
                          >
                            <FaCheckCircle /> Godkend
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDecision(req.id, 'Afslået')}
                            className="flex-1 bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded flex items-center justify-center gap-1"
                          >
                            <FaTimesCircle /> Afslå
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {settingsOpen && isAdmin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-lg p-6 relative">
            <button
              type="button"
              onClick={() => setSettingsOpen(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <FaTimes />
            </button>
            <div className="flex items-center gap-2 mb-4">
              <FaCog className="text-falck-blue" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Afspadsering indstillinger</h3>
            </div>
            {settingsError && <div className="mb-3 p-2 bg-red-100 text-red-800 rounded text-sm">{settingsError}</div>}
            {settingsMessage && <div className="mb-3 p-2 bg-green-100 text-green-800 rounded text-sm">{settingsMessage}</div>}
            <form onSubmit={handleSaveSettings} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">E-mail til godkendelse</label>
                <input
                  type="email"
                  required
                  value={approverEmail}
                  onChange={(e) => setApproverEmail(e.target.value)}
                  className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-falck-blue"
                  placeholder="leder@example.com"
                />
                <p className="text-xs text-gray-500 mt-1">Godkendelseslink sendes til denne adresse.</p>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Helligdage & fridage</label>
                  <button
                    type="button"
                    onClick={addHolidayDraft}
                    className="text-sm px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-700"
                  >
                    Tilføj dag
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Disse datoer markeres i kalenderen og tæller ikke med i beregningen.</p>
                <div className="mt-2 space-y-2 max-h-64 overflow-y-auto pr-1">
                  {holidayDrafts.length === 0 && (
                    <div className="text-xs text-gray-500">Ingen fridage tilføjet endnu.</div>
                  )}
                  {holidayDrafts.map((holiday, idx) => (
                    <div key={`${holiday.date}-${idx}`} className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-center">
                      <input
                        type="date"
                        value={holiday.date || ''}
                        onChange={(e) => updateHolidayDraft(idx, 'date', e.target.value)}
                        className="border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-falck-blue"
                        required
                      />
                      <input
                        type="text"
                        value={holiday.label || ''}
                        onChange={(e) => updateHolidayDraft(idx, 'label', e.target.value)}
                        placeholder="Titel (fx. Juleaftensdag)"
                        className="border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-falck-blue sm:col-span-2"
                      />
                      <button
                        type="button"
                        onClick={() => removeHolidayDraft(idx)}
                        className="text-sm text-red-600 hover:text-red-800 justify-self-start sm:justify-self-end"
                      >
                        Fjern
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setSettingsOpen(false)}
                  className="px-3 py-2 text-sm rounded-md border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Annuller
                </button>
                <button
                  type="submit"
                  disabled={settingsSaving || settingsLoading}
                  className="px-4 py-2 text-sm font-semibold rounded-md bg-falck-blue text-white hover:bg-falck-dark-blue disabled:opacity-60"
                >
                  {settingsSaving ? 'Gemmer...' : 'Gem' }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedEntry && isAdmin && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 overflow-y-auto">
          <div className="min-h-full flex items-start justify-center px-4 py-8">
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-2xl p-6 relative">
              <button
                type="button"
                onClick={closeEntryModal}
                className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <FaTimes />
              </button>
              <div className="flex items-center gap-2 mb-4">
                <FaUsers className="text-falck-blue" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Administrer afspadsering</h3>
              </div>
              <div className="mb-4 space-y-1 text-sm text-gray-700 dark:text-gray-300">
                <div className="font-semibold">{selectedEntry.display_name} {selectedEntry.crew_number && <span className="text-xs text-gray-500">#{selectedEntry.crew_number}</span>}</div>
                <div>Status: {statusBadge(selectedEntry.status)}</div>
                <div>Periode: {formatRangeWithTime(selectedEntry.start_date, selectedEntry.end_date, selectedEntry.start_time, selectedEntry.end_time, selectedEntry.start_day_end_time)} ({selectedEntry.days} dage)</div>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Periode</label>
                <RangeCalendar
                  startDate={editData.startDate}
                  endDate={editData.endDate}
                  holidaySet={holidaySet}
                  holidayByDate={holidayByDate}
                  onHolidaySelect={setEditHolidayNotice}
                  onChange={({ startDate, endDate }) => setEditData({ ...editData, startDate, endDate })}
                />
                {editHolidayNotice && (
                  <div className="text-xs text-amber-700 dark:text-amber-300 mt-1">Helligdag/fridag: {editHolidayNotice}</div>
                )}
              </div>
              <div className="mt-4">
                <div className={`grid grid-cols-1 ${isEditMultiDay ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-2 mb-4`}>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Starttid (valgfri)</label>
                    <input
                      type="time"
                      value={editData.startTime}
                      onChange={(e) => setEditData({ ...editData, startTime: e.target.value })}
                      className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-falck-blue"
                    />
                  </div>
                  {isEditMultiDay && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Sluttid første dag (valgfri)</label>
                      <input
                        type="time"
                        value={editData.startDayEndTime}
                        onChange={(e) => setEditData({ ...editData, startDayEndTime: e.target.value })}
                        className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-falck-blue"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                      {isEditMultiDay ? 'Sluttid sidste dag (valgfri)' : 'Sluttid (valgfri)'}
                    </label>
                    <input
                      type="time"
                      value={editData.endTime}
                      onChange={(e) => setEditData({ ...editData, endTime: e.target.value })}
                      className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-falck-blue"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mb-2">
                  Starttid gælder første dag. Sluttid sidste dag gælder kun for den sidste dag. Ved delvis første dag kan du angive sluttid første dag.
                </p>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Kommentar</label>
                <textarea
                  rows={3}
                  value={editData.comment}
                  onChange={(e) => setEditData({ ...editData, comment: e.target.value })}
                  className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-falck-blue"
                />
                <p className="text-xs text-gray-500 mt-1">Hvis datoer ændres sendes anmodningen til godkendelse igen ({editDays} hverdage).</p>
              </div>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={handleDeleteEntry}
                  disabled={entryActionLoading}
                  className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-md font-semibold disabled:opacity-60"
                >
                  <FaTrash /> Slet
                </button>
                <button
                  type="button"
                  onClick={handleResetEntry}
                  disabled={entryActionLoading}
                  className="flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-2 rounded-md font-semibold disabled:opacity-60"
                >
                  <FaUndo /> Flyt til Afventer
                </button>
                <button
                  type="button"
                  onClick={handleUpdateEntry}
                  disabled={entryActionLoading}
                  className="flex items-center justify-center gap-2 bg-falck-blue hover:bg-falck-dark-blue text-white px-3 py-2 rounded-md font-semibold disabled:opacity-60"
                >
                  <FaPen /> Gem ændringer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Afspadsering;
